import React, { useContext, useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useQuery, useMutation } from "@apollo/client";
import { GET_FAVORITE, GET_FAVORITE_ITEMS, USER_DATA } from "../querys";
import { DELETE_FAVORITE, UPDATE_FAVORITE } from "../mutations";
import Loading from "../components/Loading";
import { toast } from "react-toastify";
import CardProduct from "../components/cards/CardProduct";
import Modal from "react-modal";
import { modalLink } from "../components/styled/ShooppingStyled";
import { IoMdClose } from "react-icons/io";
import SmallLoader from "../components/small-loader";
import { AuthUserContext } from "../context/AuthUser";
import { GrFavorite } from "react-icons/gr";
import axios from "axios";
import { apiBase } from "../services/appenv";
import HeartLike from "../components/cards/Style/HeartLike";

Modal.setAppElement("#___gatsby");

const Text = styled.span`
  margin: 1em 0;
  display: block;
  font-size: 1.1em;
`;

const FavoritesInfo = ({ current_user }) => {
  const { authUser } = useContext(AuthUserContext);
  const userId = authUser?.user?.id || current_user?.id;

  const [favoriteItem, setFavoriteItem] = useState({});
  const [allSelect, setAllSelect] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBtnSelectall, setShowBtnSelectall] = useState(false);
  const [urlModal, setUrlModal] = useState(false);
  const [url, setUrl] = useState("");
  const [shareTitle, setShareTitle] = useState(
    `My favorites ${new Date().toLocaleDateString()}`
  );
  const [pending, setPending] = useState(false);
  const [stateDelete, setStateDelete] = useState(false);

  const token = localStorage.getItem("token");

  const {
    data: favoriteList,
    loading: loadingFavorites,
    refetch: refetchFavorites,
  } = useQuery(GET_FAVORITE_ITEMS, {
    variables: { id: userId },
    onCompleted: (data) => {
      setFavoriteItem(data);
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (favoriteList?.favorites?.data?.[0]?.attributes?.favoritelist) {
      const totalItems =
        favoriteList.favorites.data[0].attributes.favoritelist.length;
      setAllSelect(
        selectedItems.length > 0 && selectedItems.length === totalItems
      );
    }
  }, [selectedItems, favoriteList]);

  const { data: userData, loading: queryLoading } = useQuery(USER_DATA, {
    variables: { id: userId },
  });

  const { data, loading } = useQuery(GET_FAVORITE, {
    variables: { id: current_user.id },
  });

  const [clearFavorite, { loading: loadingDelete }] = useMutation(
    DELETE_FAVORITE,
    {
      onCompleted: () => {
        toast.success("All favorites cleared successfully");
        refetchFavorites();
      },
      onError: (error) => {
        toast.error("Error clearing favorites: " + error.message);
        setStateDelete(true);
      },
    }
  );

  const [updateFavorite, { loading: loadingUpdate }] = useMutation(
    UPDATE_FAVORITE,
    {
      onError: (error) => {
        
        toast.error("An error occurred while updating favorite");
      },
    }
  );

  const getFavoriteItems = () => {
    if (!favoriteList?.favorites?.data?.[0]?.attributes?.favoritelist) {
      return [];
    }
    return favoriteList.favorites.data[0].attributes.favoritelist || [];
  };

  const favoriteItems = getFavoriteItems();

  const handleClearAllFavorite = () => {
    if (favoriteList?.favorites?.data?.[0]?.id) {
      clearFavorite({
        variables: { id: favoriteList.favorites.data[0].id },
        refetchQueries: ["getFavoriteItem"],
      });
    } else {
      toast.error("No favorites to clear");
    }
  };

  const handleRemoveFavorite = (productId) => {
    if (!favoriteList?.favorites?.data?.[0]?.id) {
      toast.error("No favorites list found");
      return;
    }

    const favoriteId = favoriteList.favorites.data[0].id;
    const currentFavorites = [...getFavoriteItems()];
    const updatedFavorites = currentFavorites.filter(
      (item) => item.productsid !== productId
    );

    const cleanedFavorites = updatedFavorites.map(
      ({ __typename, ...rest }) => rest
    );

    updateFavorite({
      variables: {
        id: favoriteId,
        data: {
          favoritelist: cleanedFavorites,
        },
      },
      refetchQueries: ["getFavoriteItem"],
      onCompleted: () => {
        toast.success("Your favorites were updated");
      },
    });

    if (selectedItems.includes(productId)) {
      setSelectedItems(selectedItems.filter((item) => item !== productId));
    }
  };

  const handleSelectAll = () => {
    if (allSelect) {
      setSelectedItems([]);
    } else {
      const items = getFavoriteItems();
      const validProductIds = items
        .filter((element) => element?.productsid)
        .map((element) => element.productsid);
      setSelectedItems(validProductIds);
    }
  };

  const handleCheckItem = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      if (!selectedItems.includes(value)) {
        setSelectedItems([...selectedItems, value]);
      }
    } else {
      setSelectedItems(selectedItems.filter((item) => item !== value));
    }
  };

  const generateUniqueShareCode = () => {
    const timestamp = new Date().getTime();

    const randomChars = Math.random().toString(36).substring(2, 8);

    return `${timestamp}-${randomChars}`;
  };

  const handleMakeLink = async () => {
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one product");
      return;
    }

    setPending(true);
    setUrlModal(true);

    const uniqueShareCode = generateUniqueShareCode();

    try {
      const response = await axios.post(
        `${apiBase}/api/shared-favorit-lists`,
        {
          data: {
            shareCode: uniqueShareCode,
            productIds: selectedItems,
            title: shareTitle,
            users_permissions_user: userId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data?.data?.attributes?.shareCode) {
        setUrl(response.data.data.attributes.shareCode);
      } else {
      }
    } catch (error) {
      toast.error(
        "There was an error generating the favorite list. Please try again later."
      );
      setUrlModal(false);
    } finally {
      setPending(false);
    }
  };

  const handleClick = async () => {
    const shareUrl = `${window.location.origin}/share?q=${url}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("URL copied to clipboard!");
    } catch (err) {
      const el = document.createElement("textarea");
      el.value = shareUrl;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();

      let successful = false;
      try {
        successful = document.execCommand("copy");
      } catch (err) {}

      document.body.removeChild(el);

      if (successful) {
        toast.success("URL copied!");
      } else {
        toast.error("Couldn't copy URL. Please copy it manually.");
      }
    }
  };

  const handleCloseModalLink = () => {
    setUrlModal(false);
    setShareTitle(`Shared Favorites ${new Date().toLocaleDateString()}`);
  };

  const resetSelection = () => {
    setSelectedItems([]);
    setShowBtnSelectall(false);
    setAllSelect(false);
  };

  const isLoading =
    loading || queryLoading || loadingFavorites || loadingUpdate;

  return (
    <>
      {isLoading && <Loading />}

      {favoriteList ? (
        <div className="container">
          {loadingDelete && <Loading />}
          <div className="container-full">
            <div className="content-btn-favorite">
              {favoriteItems.length > 0 ? (
                <Text>{favoriteItems.length} found favorites</Text>
              ) : null}

              {favoriteItems.length > 0 ? (
                <div className="u-display-flex" style={{ flexWrap: "wrap" }}>
                  {selectedItems.length > 0 && showBtnSelectall ? (
                    <button
                      style={{ width: "140px" }}
                      type="button"
                      onClick={handleMakeLink}
                      className="btn u-text-center btn-outline u-margin-right-1 u-margin-top-1 button-out-margin-bottom"
                    >
                      MAKE LINK
                      {selectedItems.length > 0 && (
                        <span className="badge">
                          <b>
                            {"("}
                            {selectedItems.length}
                            {")"}
                          </b>
                        </span>
                      )}
                    </button>
                  ) : null}

                  {!showBtnSelectall ? (
                    <button
                      style={{ width: "100px" }}
                      type="button"
                      onClick={() => setShowBtnSelectall(true)}
                      className="btn u-text-center btn-outline u-margin-right-1 u-margin-top-1 button-out-margin-bottom"
                    >
                      SHARE
                    </button>
                  ) : (
                    <>
                      <button
                        style={{ width: "130px" }}
                        className="btn u-text-center btn-outline u-margin-right-1 u-margin-top-1 button-out-margin-bottom"
                        onClick={handleSelectAll}
                      >
                        {allSelect ? "UNCHECK ALL" : "SELECT ALL"}
                      </button>
                      <button
                        style={{ width: "100px" }}
                        className="btn u-text-center btn-danger u-margin-right-1 button-clear-danger"
                        onClick={resetSelection}
                      >
                        CANCEL
                      </button>
                    </>
                  )}

                  <button
                    style={{ width: "100px" }}
                    disabled={favoriteItems.length === 0}
                    className="btn btn-danger u-margin-right-1 button-clear-danger"
                    onClick={handleClearAllFavorite}
                  >
                    Clear All
                  </button>
                </div>
              ) : (
                <div className="no-favorite-data">
                  <GrFavorite />
                  <p>Add a favorite to your list to see it here</p>
                </div>
              )}
            </div>
            {showBtnSelectall && (
              <div style={{ width: "100%", marginBottom: "15px" }}>
                <label className="form-label">
                  Name for your favorite collection
                </label>{" "}
                <br />
                <input
                  style={{
                    height: "35px",
                    fontSize: "16px",
                    marginTop: "10px",
                    width: "270px",
                  }}
                  type="text"
                  className="form-control"
                  value={shareTitle}
                  onChange={(e) => setShareTitle(e.target.value)}
                  placeholder="Type your favorite collection"
                />
              </div>
            )}

            <div className={`row ${showBtnSelectall ? "select-active" : ""}`}>
              {favoriteItems.map((card, i) => (
                <div
                  className="col-xl-3 col-lg-4 col-md-4 col-6 u-margin-bottom-3 u-position-relative"
                  key={i}
                >
                  {showBtnSelectall && (
                    <div className="content-checkbox-mark">
                      <label className="checkbox">
                        <input
                          id={card?.productsid}
                          type="checkbox"
                          value={card?.productsid}
                          checked={selectedItems.includes(card?.productsid)}
                          onChange={handleCheckItem}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </div>
                  )}

                  <div className="content-like-heart">
                    <button
                      className="btn-heart-favorite mark-heart-check"
                      onClick={() => handleRemoveFavorite(card?.productsid)}
                    >
                      <HeartLike />
                    </button>
                  </div>

                  <CardProduct confirmed={0} data={card} like_button={false} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <Modal
        isOpen={urlModal}
        style={modalLink}
        onRequestClose={handleCloseModalLink}
      >
        <div className="container">
          <button className="btn-close-link" onClick={handleCloseModalLink}>
            <IoMdClose />
          </button>
          <div className="row content-modal-check-fields">
            <p className="title-alignament">This is your shared link</p>

            {pending ? (
              <SmallLoader />
            ) : (
              <>
                <div style={{ width: "85%", paddingRight: "30px" }}>
                  <a
                    href={`${window.location.origin}/share?q=${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wrap-text"
                    style={{ color: "#2241bb" }}
                  >
                    <p className="wrap-text">
                      {`${window.location.origin}/share?q=${url}`}
                    </p>
                  </a>
                </div>
                <button
                  style={{ width: "13%" }}
                  className="btn u-text-center btn-outline"
                  onClick={handleClick}
                >
                  Copy
                </button>
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FavoritesInfo;
